import { Component, FC, MouseEvent, ReactNode } from 'react';
import { RelayProp, graphql, createFragmentContainer } from 'react-relay';
import { connect, DispatchProp } from 'react-redux';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button';

import ArrowRight from 'dibs-icons/exports/legacy/ArrowRight';
import makeOfferLauncher from 'dibs-buyer-layout/exports/makeOfferLauncher';
// mutations
import { SharedModifyPortfolioItems } from '../../mutations/FindingSharedModifyPortfolioItems';

// styles
import styles from './PdpSharedButtons.scss';

// helpers
import { redirectToOMT } from './utils';
import { Maybe } from 'dibs-ts-utils/exports/Maybe';
import {
    trackMakeOfferClick,
    trackMakeOfferFromEmail,
    Location,
} from '../tracking/actionButtonsTracking';
import { setCartItems } from '../../actions/sharedMobileActions';
import { updateMakeOfferModalShown } from '../../actions/pdpRespMakeOfferModalActions';
import { getQueryParam } from '../../utils/uriUtils';

import { PdpSharedMakeOfferButton_item$data } from './__generated__/PdpSharedMakeOfferButton_item.graphql';
import { PdpSharedMakeOfferButton_user$data } from './__generated__/PdpSharedMakeOfferButton_user.graphql';
import { PdpSharedMakeOfferButton_viewer$data } from './__generated__/PdpSharedMakeOfferButton_viewer.graphql';

type DefaultProps = {
    size: 'small' | 'medium' | 'large';
    showArrow: boolean;
    hasPrivateOffer?: Maybe<boolean>;
    shouldLaunchContactDealer?: boolean;
};

type Props = {
    userId?: string | null;
    itemId: string;
    selectedSkuId?: string;
    item: PdpSharedMakeOfferButton_item$data;
    currency: string;
    relay: RelayProp;
    user: PdpSharedMakeOfferButton_user$data | null | undefined;
    quantitySelected?: string;
    gaLabel?: string;
    ctaCopy?: ReactNode;
    location?: Location;
    viewer: PdpSharedMakeOfferButton_viewer$data | null | undefined;
} & DefaultProps &
    DispatchProp;

const MakeOfferCTA: FC<{
    showArrow?: Maybe<boolean>;
    ctaCopy?: ReactNode;
}> = ({ showArrow, ctaCopy }) => (
    <div className={styles.makeOfferButton}>
        {ctaCopy || (
            <FormattedMessage
                id="PdpSharedMakeOfferButton.makeOfferCta"
                defaultMessage="Make An Offer"
            />
        )}
        {showArrow && <ArrowRight className={styles.arrowSVG} />}
    </div>
);

type State = {
    isModalOpen: boolean;
};

let makeOfferModalShown = false;

class PdpSharedMakeOfferButtonComponentClass extends Component<Props, State> {
    static defaultProps: DefaultProps = {
        size: 'large',
        showArrow: true,
        hasPrivateOffer: false,
        shouldLaunchContactDealer: true,
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            isModalOpen: false,
        };

        this.onClick = this.onClick.bind(this);
    }

    componentDidMount(): void {
        const queryParams = global?.location?.search;
        const modalParam = getQueryParam(queryParams, 'modal');
        const templateParam = getQueryParam(queryParams, 'utm_campaign');
        /**
         * This component might be used in multiple places on the PDP, as well as be mounted more
         * than once. Use `makeOfferModalShown` to avoid showing more than one modal at a time.
         */
        if (modalParam === 'offer' && !makeOfferModalShown) {
            if (!this.state.isModalOpen) {
                this.onClick();
            }

            if (templateParam) {
                trackMakeOfferFromEmail(templateParam, {
                    pdpTrackingVpvParams: this.props.item.makeOfferVpvParams,
                });
            }
        }
    }

    onClick(event?: MouseEvent): void {
        const {
            dispatch,
            userId,
            itemId,
            selectedSkuId,
            relay,
            quantitySelected,
            currency,
            hasPrivateOffer,
            shouldLaunchContactDealer,
            gaLabel,
            location,
            viewer,
        } = this.props;
        const { makeOfferVpvParams: pdpTrackingVpvParams, ...item } = this.props.item;
        trackMakeOfferClick({
            item: { ...item, pdpTrackingVpvParams },
            hasPrivateOffer,
            event,
            location,
            environment: relay.environment,
            quantity: Number(quantitySelected),
        });
        if (!viewer?.multiItemCartFF) {
            if (!userId) {
                // If no user, store item - it can be ADDed to cart when user logs in later
                dispatch(setCartItems(itemId));
            } else {
                // ADD item to user's cart
                SharedModifyPortfolioItems.commit(relay.environment, {
                    action: 'ADD',
                    itemId,
                    portfolioType: 'CART',
                    userId,
                });
            }
        }

        const { serviceId: transactionId, hasCompletedCheckout } =
            item?.lastActiveTransaction || {};

        if (hasPrivateOffer && transactionId && hasCompletedCheckout) {
            redirectToOMT(transactionId);
        } else {
            this.setState(
                {
                    isModalOpen: true,
                },
                () => {
                    makeOfferModalShown = true;
                    makeOfferLauncher({
                        itemId,
                        selectedSkuId,
                        currency,
                        environment: relay.environment,
                        quantitySelected,
                        shouldLaunchContactDealer,
                        launchOrigin: 'pdp-shared',
                        gaLabel,
                        page: 'pdp',
                        onClose: () => dispatch(updateMakeOfferModalShown(false)),
                        onOpen: () => dispatch(updateMakeOfferModalShown(true)),
                    });
                }
            );
        }
    }

    render(): ReactNode {
        const { size, showArrow, item, ctaCopy, hasPrivateOffer } = this.props;
        const isPrivateVisibilityType = item?.isPrivateVisibilityType;
        return (
            <Button
                type={isPrivateVisibilityType ? 'secondaryAlt' : 'secondary'}
                className={styles.button}
                dataTn="item-actions-make-offer"
                onClick={this.onClick}
                size={size}
                fullWidth
            >
                <MakeOfferCTA
                    showArrow={showArrow && size !== 'small'}
                    ctaCopy={
                        hasPrivateOffer ? (
                            <FormattedMessage
                                id="PdpSharedMakeOfferButton.counterOffer"
                                defaultMessage="Counter Offer"
                            />
                        ) : (
                            ctaCopy
                        )
                    }
                />
            </Button>
        );
    }
}

export const PdpSharedMakeOfferButtonComponent = connect()(PdpSharedMakeOfferButtonComponentClass);

export const PdpSharedMakeOfferButton = createFragmentContainer(PdpSharedMakeOfferButtonComponent, {
    viewer: graphql`
        fragment PdpSharedMakeOfferButton_viewer on Viewer {
            multiItemCartFF: featureFlag(feature: "multiItemCart")
        }
    `,
    item: graphql`
        fragment PdpSharedMakeOfferButton_item on Item
        @argumentDefinitions(pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: pdp }) {
            ...SharedContactDealerWrapper_item
            ...actionButtonsTracking_item
                @arguments(fetchAuction: $fetchAuction, pageDisplayEnum: $pageDisplayEnum)
            makeOfferVpvParams: pdpTrackingVpvParams(
                userId: $userId
                excludedPurchaseStatuses: [RET, CUST]
                fdfQuoteABTestVariant: $fdfQuoteABTestVariant
            ) @include(if: $afterDisplay)
            isPrivateVisibilityType
            lastActiveTransaction(buyerId: $userId) {
                serviceId
                hasCompletedCheckout
            }
        }
    `,
    user: graphql`
        fragment PdpSharedMakeOfferButton_user on User {
            ...SharedContactDealerWrapper_user
        }
    `,
});
